import api from './api';
import type { CredentialFieldSchema } from './integrations';

export type PluginEnv = 'sandbox' | 'production';
/** Where a switch's effective value comes from: a server env flag, a country
 *  admin's row, the plug-in's declared default, or no plug-in at all. */
export type SwitchSource = 'env' | 'switch' | 'default' | 'ungoverned';

export interface SwitchState {
  enabled: boolean;
  source: SwitchSource;
  env_flag: string;
  env_override: boolean | null;
  stored: boolean | null;
}

export interface PluginCapabilities {
  vehicle_categories: string[];
  covers: string[];
  online_purchase: boolean;
  issuance_mode: 'auto' | 'awaiting_verification';
  auto_issue_setting: string;
  addon_codes: string[];
  returns_idv_band: boolean;
}

export interface PluginLine {
  line: string;
  capabilities: PluginCapabilities;
  effective_issuance_mode: 'auto' | 'awaiting_verification';
  default_enabled: boolean;
  env_flag: string;
  switches: Record<PluginEnv, SwitchState>;
}

export interface PluginMastersImporter {
  command: string;
  sections: string[];
  supports_dry_run: boolean;
  description: string;
}

export interface PluginIntegrationConfig {
  exists: boolean;
  is_enabled: boolean;
  active_api_version: string;
}

export interface InsurerPluginRow {
  code: string;
  display_name: string;
  api_version: string;
  insurer_id: string | null;
  insurer_name: string;
  relay_required: boolean;
  hosts: { uat: string[]; prod: string[] };
  seed_command: string;
  service_endpoint_names: string[];
  masters: PluginMastersImporter[];
  credential_fields: CredentialFieldSchema[];
  credential_coverage: Record<PluginEnv, number>;
  integration_configs: Record<PluginEnv, PluginIntegrationConfig>;
  lines: PluginLine[];
}

export interface InsurerPluginsResponse {
  count: number;
  results: InsurerPluginRow[];
  /** Plug-ins whose plugin.py failed to load: module -> error. Not in use. */
  discovery_errors: Record<string, string>;
}

export interface SwitchWrite {
  insurer_code: string;
  line: string;
  environment: PluginEnv;
  enabled: boolean;
}

/** The EFFECTIVE state after the write; `detail` says when an env flag overrides it. */
export interface SwitchWriteResult extends SwitchState {
  insurer_code: string;
  line: string;
  environment: PluginEnv;
  detail?: string;
}

export interface LiveInsurer {
  code: string;
  display_name: string;
}

export const insurerPluginsService = {
  async list(): Promise<InsurerPluginsResponse> {
    const response = await api.get('/integrations/admin/insurer-plugins/');
    return response.data;
  },

  async setSwitch(input: SwitchWrite): Promise<SwitchWriteResult> {
    const response = await api.post('/integrations/admin/insurer-plugins/switches/', input);
    return response.data;
  },

  /** Insurers switched on for a line (any environment): for pickers. */
  async liveInsurers(line = 'motor'): Promise<LiveInsurer[]> {
    const response = await api.get('/integrations/live-insurers/', { params: { line } });
    return response.data.results;
  },
};
